<template>
  <b-card class="p-1">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
      <div class="d-flex flex-column flex-md-row w-100 w-md-75 mb-2 mb-md-0">
        <b-form-group class="mb-2 mb-md-0 mr-md-3 flex-grow-1">
          <b-form-label>Wyszukaj</b-form-label>
          <b-input-group size="sm">
            <b-form-input
              id="filterInput"
              v-model="filter"
              type="search"
              placeholder="Wyszukaj po nazwie"
              @input="debouncedSearch" />
            <b-input-group-append>
              <b-button :disabled="!filter" @click="clearSearch"> Wyczyść </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group class="mb-2 mb-md-0 mr-md-2">
          <b-form-label>Kraj</b-form-label>
          <b-form-select
            v-model="country"
            :options="countryOptions"
            size="sm"
            @change="handleCountryChange"
          ></b-form-select>
        </b-form-group>

        <b-form-group class="mb-2 mb-md-0">
          <b-form-label>Typ</b-form-label>
          <b-form-select
            v-model="type"
            :options="typeOptions"
            size="sm"
            @change="handleTypeChange"
          ></b-form-select>
        </b-form-group>

      </div>
      <div class="d-flex w-100 w-md-auto justify-content-center justify-content-md-end">
        <b-button
        :to="{ name: 'shops-create' }"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        type="submit"
        variant="primary"
        class="btn">
        Dodaj
      </b-button>
    </div>
    </div>
    <b-table
      responsive
      bordered
      striped
      hover
      :items="shops"
      :fields="fields"
      :busy="isLoading">
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong> Wczytywanie...</strong>
        </div>
      </template>
      <template #cell(name)="row">
        <span>
          {{ row.item.name.length > 45 ? row.item.name.slice(0, 45) + '...' : row.item.name }}
        </span>
      </template>
      <template #cell(country)="row">
        <span v-if="row.item.country === 'PL'">Polska</span>
        <span v-if="row.item.country === 'UA'">Ukraina</span>
      </template>
      <template #cell(localization)="row">
        <div class="d-flex flex-column">
           <span>Długość: {{ row.item.geo_location.latitude }}</span>
           <span>Szerokość: {{ row.item.geo_location.longitude }}</span>
        </div>
      </template>
      <template #cell(should_update)="row"> 
        <div class="d-flex align-items-center">
          <b-form-checkbox v-model="row.item.should_update" @change="handleToggleUpdate(row.item.original_id)" />
          <span v-if="row.item.should_update">Tak</span>
          <span v-else>Nie</span>
        </div>
      </template>
      <template #cell(is_online)="row">
        <div class="d-flex align-items-center">
          <span v-if="row.item.is_online">Tak</span>
          <span v-else>Nie</span>
        </div>
      </template>
      <template #cell(actions)="row">
        <div class="d-flex flex-column align-items-center">
          <b-button
            :to="{ name: 'shops-edit', params: { id: row.item.original_id } }"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="btn table-btn"
            size="sm">
            Edytuj
          </b-button>
          <b-button class="mt-1 table-btn" variant="danger" size="sm" @click="deleteShop(row.item)">
            Usuń
          </b-button>
        </div>
      </template>
    </b-table>
    
    <!-- Pagination controls -->
    <div class="mt-2 d-flex justify-content-between align-items-center">
      <span>Pokazuje {{ shops.length }} z {{ total }} rekordów</span>
      <b-pagination
        v-model="page"
        :total-rows="total"
        :per-page="perPage"
        @change="handlePageChange"
        aria-controls="shops-table"
        class="mb-0"
      ></b-pagination>
      <b-form-select
        v-model="perPage"
        :options="perPageOptions"
        @change="handlePerPageChange"
        size="sm"
        class="w-auto"
      ></b-form-select>
    </div>
  </b-card>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';

import { all, destroy, updateShouldUpdate } from '@/http/shops';
import { BTable, BFormGroup, BInputGroup, BInputGroupAppend, BFormInput, BFormSelect, BPagination, BSpinner } from 'bootstrap-vue';

export default {
  mixins: [NotificationMixin],
  components: {
    BTable,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BFormSelect,
    BPagination,
    BSpinner,
  },

  data() {
    return {
      shops: [],
      filter: '',
      country: '',
      type: '',
      page: 1,
      perPage: 10,
      total: 0,
      fields: [
        { key: 'name', label: 'Nazwa', sortable: true },
        { key: 'country', label: 'Kraj', sortable: true },
        { key: 'localization', label: 'Lokalizacja', sortable: true },
        { key: 'should_update', label: 'Nadpisuj danymi z TETA', sortable: true },
        { key: 'is_online', label: 'Sklep online', sortable: true },
        { key: 'actions', label: 'Akcje' },
      ],
      perPageOptions: [10, 25, 50, 100],
      countryOptions: [
        { value: '', text: 'Wszystkie' },
        { value: 'PL', text: 'Polska' },
        { value: 'UA', text: 'Ukraina' },
      ],
      typeOptions: [
        { value: '', text: 'Wszystkie' },
        { value: 'online', text: 'Online' },
        { value: 'offline', text: 'Stacjonarne' },
      ],
      searchTimeout: null,
      isLoading: false,
    };
  },

  async mounted() {
    await this.fetchShops();
  },

  methods: {
    async handleToggleUpdate(shopId) {
      try {
        await updateShouldUpdate(shopId);
        this.showSuccessNotification('Sklep zaktualizowany');
      } catch (error) {
        this.showErrorNotification(
          'Problem z aktualizacją sklepu',
          'Wystąpił błąd podczas aktualizacji sklepu. Skontaktuj się ze swoim developerem.'
        );
      }
    },

    async fetchShops() {
      try {
        this.isLoading = true;
        const params = {
          lang: 'pl',
          page: this.page,
          perPage: this.perPage,
          query: this.filter,
          country: this.country,
          type: this.type,
        };

        const { data } = await all(params);

        this.shops = data.data;
        this.total = data.meta.total;
        this.perPage = data.meta.per_page;
        this.page = data.meta.current_page;
      } catch (error) {
        this.showErrorNotification(
          'Problem z pobraniem danych',
          'Wystąpił błąd podczas pobierania sklepów. Skontaktuj się ze swoim developerem.',
        );
      } finally {
        this.isLoading = false;
      }
    },

    debouncedSearch() {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Set a new timeout
      this.searchTimeout = setTimeout(() => {
        this.page = 1; // Reset to first page when search changes
        this.fetchShops();
      }, 500); // 500ms debounce time
    },

    clearSearch() {
      this.filter = '';
      this.page = 1;
      this.fetchShops();
    },

    handlePageChange(page) {
      this.page = page;
      this.fetchShops();
    },

    handlePerPageChange() {
      this.page = 1; // Reset to first page when changing items per page
      this.fetchShops();
    },

    handleCountryChange() {
      this.page = 1; // Reset to first page when changing country
      this.fetchShops();
    },

    handleTypeChange() {
      this.page = 1; // Reset to first page when changing type
      this.fetchShops();
    },

    deleteShop(shop) {
      this.$swal({
        title: 'Jesteś pewny?',
        text: 'Czy chcesz usunąć ten sklep? Tej operacji nie można cofnąć.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          destroy(shop.original_id)
            .then(() => {
              this.showSuccessNotification('Usunięto sklep', 'Sklep został usunięty.');
              this.fetchShops();
            })
            .catch(() => {
              this.showErrorNotification(
                'Problem z usunięciem sklepu',
                'Wystąpił błąd podczas usuwania. Skontaktuj się ze swoim developerem.',
              );
            });
        }
      });
    },
  },
};
</script>

<style lang="scss"></style>
